import { useGlobalComponentsContentfulQuery } from '@snapchat/mw-global-components-schema';
import {
  BackgroundColor,
  BrowserFeaturesContext,
  Button,
  GlobalHeaderContext,
  GlobalHeader as GlobalHeaderSDS,
  isMobileOs,
  GlobalNavItem as ItemMobileSDS,
  Logo as LogoSDS,
  mobileMaxWidth,
  NavigatorItemDesktop as NavigatorItemDesktopSDS,
  NavigatorItemMobile as NavigatorItemMobileSDS,
  PrimitivesProvider,
} from '@snapchat/snap-design-system-marketing';
import head from 'lodash-es/head';
import type { FC, ReactNode } from 'react';
import { Children, useContext, useMemo } from 'react';

import { Action } from '../../analytics/Action';
import { defaultLocaleCode, GlobalComponentsContext } from '../../context/GlobalComponentsContext';
import type { GlobalNavAllType, GlobalNavConfigType } from '../../generated/contentful-types';
import { GlobalNavConfigCollectionQueryDocumentType } from '../../generated/contentful-types';
import { hightlightSizedSrcSets } from '../GlobalNavHighlight/GlobalNavHighlight';
import { GlobalNavGroupScreen } from '../GlobalNavScreen/GlobalNavScreen';
import type { MediaPreloadSettings } from '../Media/mediaUtils';
import { useMediaPreload } from '../Media/useMediaPreload';
import type { GlobalHeaderProps } from './types';

const defaultBackgroundColor = BackgroundColor.Black;
const defaultDefaultGroupKey = 'snapchat';

// Test to see if there are duplicate contexts.
// Explanation: We set this value here as a type of invariant. Users of this package
// Can see if this value is set to test if they have a different version of SDSM installed.
// TODO: Make a separate file containing setting and testing logic.
// Tracking: https://jira.sc-corp.net/browse/ENTWEB-4284
GlobalHeaderContext.displayName = 'GlobalHeaderContextSDSM';

const mediaPreloadSettings: MediaPreloadSettings = {
  sizedSrcSets: hightlightSizedSrcSets,
  skipPreload: () => {
    if (!window) return true;
    return window.innerWidth <= mobileMaxWidth;
  },
};

/** Navigation Header with a global nav available on click. */
export const GlobalHeader: FC<GlobalHeaderProps> = ({
  motifScheme,
  backgroundColor,
  className,
  defaultGroupKey,
  siteName,
  displayed,
  trackingSiteName,
  cta,
  ctaItems,
  localNavDesktop,
  localNavMenuItems,
  logo,
  logoProps,
  localNavMobile,
  localNavMobileFooter,
  onToggleExpanded,
  search,
  searchOpen,
  endChildrenDesktopClassName,
  endChildrenMobileClassName,
  showGlobalLinks = true,
  showNavScreen,
  pathname,
  navItemAlignment,
  customIcon,
  lessMargins,
  addBottomBorder,
  headerClassName,
  onGlobalNavGroupSummaryClick,
}) => {
  const context = useContext(GlobalComponentsContext);
  const { Anchor, onEvent } = context;

  const { data } = useGlobalComponentsContentfulQuery(
    GlobalNavConfigCollectionQueryDocumentType,
    {
      currentLocale: context.currentLocale ?? defaultLocaleCode,
      isPreview: context.isPreview ?? false,
      isSSR: context.isSSR ?? false,
    },
    { client: context.globalApolloClient }
  );

  const config = head(data?.globalNavConfigCollection.items) as GlobalNavConfigType;
  const navGroups = useMemo(
    () => config?.globalNavGroupsCollection.items ?? [],
    [config?.globalNavGroupsCollection.items]
  );

  const browserFeatures = useContext(BrowserFeaturesContext);
  const isMobile = isMobileOs(browserFeatures);

  // we have to memo this otherwise we "preload" on every render since its a new array every time
  const assetsToPreload = useMemo(
    () =>
      showGlobalLinks
        ? navGroups.map(item => item.highlight?.background).filter(asset => !!asset)
        : [],
    [navGroups, showGlobalLinks]
  );
  useMediaPreload(assetsToPreload, false, mediaPreloadSettings);

  // Track expansion.
  const onToggleExpandedWithLogging = (isExpanded: boolean): void => {
    onEvent?.({
      component: 'GlobalHeader',
      action: Action.Click,
      label: isExpanded ? 'expand' : 'collapse',
    });
    onToggleExpanded?.(isExpanded);
  };

  const callToActions =
    cta ??
    Children.toArray(
      ctaItems?.map((item, id) => (
        <Button key={id} link={item.url} type={item.type} size="Compact" onClick={item.onClick}>
          {item.title}
        </Button>
      ))
    );

  // TODO: Remove the concept of "search" from this version. Does not belong in exported nav.
  const endChildren = searchOpen ? (
    search
  ) : (
    <>
      {callToActions}
      {search}
    </>
  );

  const defaultLogo = logoProps ? <LogoSDS {...logoProps} logoType={'Ghost'} /> : null;

  const shouldShowLogo = !(searchOpen && isMobile);

  const endChildrenClassName = isMobile ? endChildrenMobileClassName : endChildrenDesktopClassName;

  let mobileNavigation: ReactNode;
  let desktopNavigation: ReactNode;

  // Determine which mobile nav to use
  if (localNavMobile) {
    // If nav component passed
    mobileNavigation = localNavMobile;
  } else if (localNavMenuItems) {
    // If props passed for new mega-menu
    mobileNavigation = localNavMenuItems?.map(
      ({
        id,
        url,
        title,
        isSelected,
        col1Label,
        col1Items,
        col2Label,
        col2Items,
        col3Label,
        col3Items,
        featuredItem,
      }) => (
        <ItemMobileSDS
          key={id}
          showExternalIcon={false}
          href={url}
          title={title}
          isSelected={isSelected}
        >
          {(col1Items?.length || col2Items?.length || col3Items?.length || !!featuredItem) && (
            <NavigatorItemMobileSDS
              col1Label={col1Label}
              col1Items={col1Items}
              col2Label={col2Label}
              col2Items={col2Items}
              col3Label={col3Label}
              col3Items={col3Items}
              featuredItem={featuredItem}
            />
          )}
        </ItemMobileSDS>
      )
    );
  }

  // Determine which desktop nav to use
  if (localNavDesktop) {
    // If nav component passed
    desktopNavigation = localNavDesktop;
  } else if (localNavMenuItems) {
    // If props passed for new mega-menu
    desktopNavigation = localNavMenuItems?.map(
      ({
        id,
        url,
        title,
        isSelected,
        col1Label,
        col1Items,
        col2Label,
        col2Items,
        col3Label,
        col3Items,
        featuredItem,
      }) => (
        <NavigatorItemDesktopSDS
          key={id}
          url={url}
          isSelected={isSelected}
          title={title ?? ''}
          col1Label={col1Label}
          col1Items={col1Items}
          col2Label={col2Label}
          col2Items={col2Items}
          col3Label={col3Label}
          col3Items={col3Items}
          featuredItem={featuredItem}
        />
      )
    );
  }

  return (
    <PrimitivesProvider value={{ Anchor }}>
      <GlobalHeaderSDS
        className={className}
        siteName={siteName}
        motifScheme={motifScheme}
        backgroundColor={backgroundColor ?? defaultBackgroundColor}
        displayed={displayed}
        logo={shouldShowLogo && (logo ?? defaultLogo)}
        onToggleExpanded={onToggleExpandedWithLogging}
        cta={endChildren}
        localNavDesktop={desktopNavigation}
        defaultGroupKey={defaultGroupKey ?? defaultDefaultGroupKey}
        stayOpenInvariant={pathname}
        trackingSiteName={trackingSiteName ?? context.hostname}
        endChildrenClassName={searchOpen ? endChildrenClassName : undefined}
        showGlobalLinks={showGlobalLinks}
        showNavScreen={showNavScreen}
        isUrlCurrent={context.isUrlCurrent}
        navItemAlignment={navItemAlignment}
        buttonAriaLabel={config?.globalNavButtonAriaLabel}
        lessMargins={lessMargins}
        addBottomBorder={addBottomBorder}
        customIcon={customIcon}
        headerClassName={headerClassName}
      >
        {config && (
          <GlobalNavGroupScreen
            motifScheme={motifScheme}
            backgroundColor={backgroundColor ?? defaultBackgroundColor}
            localNavMobile={mobileNavigation}
            localNavMobileFooter={localNavMobileFooter}
            globalNavHeading={config?.globalNavLabel}
            navGroups={navGroups as unknown as GlobalNavAllType[]}
            showMobileGlobalLinks={showGlobalLinks}
            onGlobalNavGroupSummaryClick={onGlobalNavGroupSummaryClick}
          />
        )}
      </GlobalHeaderSDS>
    </PrimitivesProvider>
  );
};

GlobalHeader.displayName = 'GlobalHeader';
